import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, HelpCircle, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { bitEvent } from "@/lib/bit";
import { useAuth } from "@/context/AuthContext";

const LETTERS = ["A", "B", "C", "D", "E"];

export default function Quiz({ questions, onGraded, alreadyCorrect = {} }) {
  const { refreshUser } = useAuth();
  const [selected, setSelected] = useState({});
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!questions || questions.length === 0) return null;

  const openQuestions = questions.filter((q) => !alreadyCorrect[q.id]);

  const choose = (qid, idx) => {
    if (results) return;
    setSelected((prev) => ({ ...prev, [qid]: idx }));
  };

  const submit = async () => {
    if (Object.keys(selected).length < openQuestions.length) {
      toast.warning("Bitte beantworte alle Fragen.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quiz/submit", { answers: selected });
      setResults(data.results);
      if (data.xp_awarded > 0) {
        toast.success(`Stark! +${data.xp_awarded} XP verdient`);
      } else {
        toast.success(`${data.correct_count}/${data.total} richtig!`);
      }
      bitEvent("quiz", {
        text: data.correct_count === data.total
          ? `Alle ${data.total} Fragen richtig!`
          : `${data.correct_count} von ${data.total} richtig. Lies dir die Erklärungen zu den falschen Antworten durch.`,
        xp: data.xp_awarded,
      });
      await refreshUser();
      onGraded?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="nb-card p-6" data-testid="quiz-section">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 bg-warning border-2 border-black rounded-lg flex items-center justify-center nb-shadow-sm">
          <HelpCircle className="h-5 w-5" strokeWidth={2.5} />
        </div>
        <h3 className="font-heading font-black text-xl">Quiz – Teste dein Wissen</h3>
      </div>

      <div className="space-y-6">
        {questions.map((q, qi) => {
          const res = results?.[q.id];
          if (alreadyCorrect[q.id] && !res) {
            return (
              <div key={q.id} data-testid={`quiz-question-${qi}`}>
                <p className="font-bold mb-3">{qi + 1}. {q.question}</p>
                <div className="flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-black font-bold bg-success/70" data-testid={`quiz-${qi}-already-correct`}>
                  <CheckCircle2 className="h-5 w-5 shrink-0" strokeWidth={2.5} />
                  Bereits richtig beantwortet
                </div>
              </div>
            );
          }
          return (
            <div key={q.id} data-testid={`quiz-question-${qi}`}>
              <p className="font-bold mb-3">{qi + 1}. {q.question}</p>
              <div className="grid gap-2">
                {q.options.map((opt, oi) => {
                  const isSel = selected[q.id] === oi;
                  let stateCls = "bg-white hover:bg-muted";
                  if (results) {
                    if (res?.correct_answer === oi) stateCls = "bg-success text-black";
                    else if (isSel && !res?.correct) stateCls = "bg-error text-white";
                    else stateCls = "bg-white opacity-70";
                  } else if (isSel) {
                    stateCls = "bg-secondary text-white";
                  }
                  return (
                    <button
                      key={oi}
                      onClick={() => choose(q.id, oi)}
                      disabled={!!results}
                      data-testid={`quiz-${qi}-option-${oi}`}
                      className={`flex items-center gap-3 text-left px-4 py-3 rounded-lg border-2 border-black font-medium transition-all ${stateCls} ${
                        !results ? "nb-press" : ""
                      }`}
                    >
                      <span className="font-black w-6 h-6 flex items-center justify-center bg-black text-white rounded text-xs shrink-0">
                        {LETTERS[oi]}
                      </span>
                      <span className="flex-1">{opt}</span>
                      {results && res?.correct_answer === oi && (
                        <CheckCircle2 className="h-5 w-5" strokeWidth={2.5} />
                      )}
                      {results && isSel && !res?.correct && (
                        <XCircle className="h-5 w-5" strokeWidth={2.5} />
                      )}
                    </button>
                  );
                })}
              </div>
              {res && (
                <p className="mt-2 text-sm font-medium bg-muted border-2 border-black rounded-lg px-3 py-2">
                  {res.correct ? "✅ Richtig! " : "❌ Nicht ganz. "}
                  {res.explanation}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {!results && openQuestions.length > 0 && (
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="quiz-submit"
          className="mt-6 flex items-center justify-center gap-2 w-full bg-primary text-white font-bold py-3 rounded-lg border-2 border-black nb-shadow nb-press disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />}
          Antworten prüfen
        </button>
      )}
    </div>
  );
}
